import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import Portal from "@mui/material/Portal";
import { Link, useLocation } from "react-router-dom";

import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";

import { useAuth } from "./AuthContext/AuthContext";
import { useMenuData } from "./SidebarMenu/useMenuData";
import { useMenuFavorites } from "./SidebarMenu/useMenuFavorites";
import { useMenuRecent } from "./SidebarMenu/useMenuRecent";
import { useLocalStorageState } from "./SidebarMenu/useLocalStorageState";
import { searchMenuTree, flattenVisibleRows, flattenAll } from "./SidebarMenu/searchUtils";
import { MenuTree } from "./SidebarMenu/MenuTree";
import { MenuSection } from "./SidebarMenu/MenuSection";
import { MenuSearchBox } from "./SidebarMenu/MenuSearchBox";
import { SearchResultsList } from "./SidebarMenu/SearchResultsList";
import { MenuItemRow } from "./SidebarMenu/MenuItemRow";
import { VirtualList } from "./SidebarMenu/VirtualList";
import {
  VIRTUALIZE_THRESHOLD,
  ROW_HEIGHT,
  DRAWER_WIDTH_EXPANDED,
  DRAWER_WIDTH_COMPACT,
  NAV_HEIGHT,
} from "./SidebarMenu/constants";

/**
 * Riel de solo íconos, SIEMPRE visible (no es un modal: no tiene backdrop y
 * no bloquea el resto de la página). Es el estado de reposo del menú — el
 * usuario navega directo desde acá sin tener que abrir nada.
 */
function IconRail({ tree, favoriteItems, recentItems, isRouteActive, hasActiveRouteInTree, isFavorite, toggleFavorite, onLeafSelect, onExpandCategory, onExpand, darkMode, onToggleDarkMode }) {
  return (
    <Box
      component="nav"
      aria-label="Menú principal (íconos)"
      className="sidebar-scroll fixed top-0 left-0 z-30 flex flex-col overflow-y-auto border-r border-slate-200 bg-slate-50 shadow-[2px_0_10px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-[#111528]"
      style={{ height: "100vh", width: DRAWER_WIDTH_COMPACT, paddingTop: NAV_HEIGHT }}
    >
      <div className="flex flex-col items-center gap-1 border-b border-slate-200 bg-morado py-2 dark:border-white/10">

        <Tooltip title="Ver menú completo" placement="right">
          <button
            type="button"
            onClick={onExpand}
            aria-label="Ver menú completo con detalle"
            className="flex h-7 w-7 items-center justify-center rounded-full text-white/80 hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <MenuOpenIcon sx={{ fontSize: 16 }} />
          </button>
        </Tooltip>
      </div>

      <List className="flex-1 py-1.5">
        {favoriteItems.map((item) => (
          <MenuItemRow
            key={`rail-fav-${item.id}`}
            item={item}
            compact
            isCurrentRouteActive={isRouteActive(item.route)}
            isFavorite
            onToggleFavorite={toggleFavorite}
            onSelect={() => onLeafSelect(item)}
          />
        ))}

        {favoriteItems.length > 0 && <div className="mx-3 my-1.5 border-t border-slate-200 dark:border-white/10" />}

        {recentItems.map((item) => (
          <MenuItemRow
            key={`rail-recent-${item.id}`}
            item={item}
            compact
            isCurrentRouteActive={isRouteActive(item.route)}
            isFavorite={isFavorite(item.id)}
            onToggleFavorite={toggleFavorite}
            onSelect={() => onLeafSelect(item)}
          />
        ))}

        {recentItems.length > 0 && <div className="mx-3 my-1.5 border-t border-slate-200 dark:border-white/10" />}

        {tree.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          return (
            <MenuItemRow
              key={`rail-${item.id}`}
              item={item}
              compact
              isCurrentRouteActive={isRouteActive(item.route)}
              isChildRouteActive={hasChildren ? item.children.some(hasActiveRouteInTree) : false}
              isFavorite={isFavorite(item.id)}
              onToggleFavorite={toggleFavorite}
              onSelect={() => (hasChildren ? onExpandCategory(item) : onLeafSelect(item))}
            />
          );
        })}
      </List>
    </Box>
  );
}

export function SwipeableTemporaryDrawer({ userDataToken }) {
  // El overlay con detalle es la única parte "temporal": arranca cerrado y
  // se colapsa solo tras elegir un módulo, porque el riel de íconos (siempre
  // visible) ya cubre la navegación del día a día.
  const [expanded, setExpanded] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [darkMode, setDarkMode] = useLocalStorageState("sidebar_dark_mode", false);

  const { setMenuId } = useAuth();
  const location = useLocation();

  const { tree, status } = useMenuData(userDataToken?.idUsuario);
  const { favoriteItems, isFavorite, toggleFavorite } = useMenuFavorites(userDataToken?.idUsuario, tree);
  const { recentItems, registerVisit } = useMenuRecent(userDataToken?.idUsuario, tree);

  const userDisplayName =
    userDataToken?.Nombre || userDataToken?.nombre || userDataToken?.Usuario || userDataToken?.usuario || "Usuario";

  const userInitials = userDisplayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  const toggleSubMenu = (id) => setOpenSubMenus((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleLeafSelect = (item) => {
    setMenuId(item.id);
    registerVisit(item.id);
    setExpanded(false);
  };

  const handleMenuSelect = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    if (hasChildren) {
      toggleSubMenu(item.id);
      return;
    }
    handleLeafSelect(item);
  };

  // Clic en una categoría (con hijos) desde el riel de íconos: no es un
  // destino navegable en sí misma, así que abre el detalle con esa
  // categoría ya expandida en vez de intentar anidar íconos en 68px.
  const handleExpandCategory = (item) => {
    setOpenSubMenus((prev) => ({ ...prev, [item.id]: true }));
    setExpanded(true);
  };

  const isRouteActive = (route) => {
    if (!route) return false;
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  };

  const hasActiveRouteInTree = (node) => {
    if (!node) return false;
    if (isRouteActive(node.route)) return true;
    return (node.children || []).some(hasActiveRouteInTree);
  };

  // Auto-expandir los ancestros de la ruta activa (al navegar directo a una
  // URL, o al refrescar la página estando en un módulo anidado).
  useEffect(() => {
    if (!tree.length) return;

    const expandedByRoute = {};
    const markParentsIfChildActive = (node) => {
      if (!node.children || node.children.length === 0) return isRouteActive(node.route);
      const childHasActive = node.children.some((child) => markParentsIfChildActive(child));
      if (childHasActive) expandedByRoute[node.id] = true;
      return childHasActive || isRouteActive(node.route);
    };

    tree.forEach(markParentsIfChildActive);
    if (Object.keys(expandedByRoute).length > 0) {
      setOpenSubMenus((prev) => ({ ...prev, ...expandedByRoute }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, location.pathname]);

  const searchResults = useMemo(() => searchMenuTree(tree, searchQuery), [tree, searchQuery]);
  const isSearching = searchQuery.trim().length > 0;

  const visibleRows = useMemo(() => flattenVisibleRows(tree, openSubMenus), [tree, openSubMenus]);
  const shouldVirtualize = visibleRows.length > VIRTUALIZE_THRESHOLD;
  // Total estable de módulos navegables (no cambia al expandir/colapsar categorías).
  const totalModuleCount = useMemo(() => flattenAll(tree).filter(({ item }) => item.route).length, [tree]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  return (
    <div className={darkMode ? "dark" : ""}>
      {status === "ready" && (
        <Portal>
          <IconRail
            tree={tree}
            favoriteItems={favoriteItems}
            recentItems={recentItems}
            isRouteActive={isRouteActive}
            hasActiveRouteInTree={hasActiveRouteInTree}
            isFavorite={isFavorite}
            toggleFavorite={toggleFavorite}
            onLeafSelect={handleLeafSelect}
            onExpandCategory={handleExpandCategory}
            onExpand={() => setExpanded(true)}
            darkMode={darkMode}
            onToggleDarkMode={toggleDarkMode}
          />
        </Portal>
      )}

      <SwipeableDrawer
        anchor="left"
        open={expanded}
        onClose={() => setExpanded(false)}
        onOpen={() => setExpanded(true)}
        PaperProps={{
          className:
            "overflow-hidden border-r border-slate-200 shadow-[0_16px_36px_rgba(15,23,42,0.2)] rounded-tr-[12px] rounded-br-[12px] dark:border-white/10",
          style: { width: DRAWER_WIDTH_EXPANDED },
        }}
      >
        <Box
          role="navigation"
          aria-label="Menú principal (detalle)"
          className="flex h-full flex-col bg-slate-50 dark:bg-[#111528]"
          style={{ width: DRAWER_WIDTH_EXPANDED }}
        >
          <div className="flex items-center justify-between border-b border-white/15 bg-morado px-3.5 py-3">
            <Link to="/dashboard" className="rounded-md" onClick={() => setExpanded(false)}>
              <img className="mx-auto w-24 cursor-pointer drop-shadow-sm lg:w-28" src="/img/logo.webp" alt="Logo Point" />
            </Link>

            <Tooltip title="Colapsar a solo íconos" placement="bottom">
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Colapsar a solo íconos"
                className="flex h-7 w-7 items-center justify-center rounded-full text-white/80 hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <ChevronRightRoundedIcon sx={{ fontSize: 20 }} className="-rotate-180" />
              </button>
            </Tooltip>
          </div>

          <div className="border-b border-slate-200 bg-white/90 px-3 py-2.5 dark:border-white/10 dark:bg-transparent">
            <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 shadow-sm dark:border-white/15 dark:bg-white/5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-morado text-xs font-bold text-white shadow-sm">
                {userInitials || "US"}
              </div>
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-slate-700 dark:text-slate-100">{userDisplayName}</p>
                <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400">Perfil activo</p>
              </div>
            </div>
          </div>

          <MenuSearchBox value={searchQuery} onChange={setSearchQuery} />

          <List className="sidebar-scroll flex-1 overflow-y-auto py-1.5">
            {status === "loading" && (
              <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">Cargando menú…</div>
            )}

            {status === "error" && (
              <div className="px-4 py-8 text-center text-sm text-red-500">No se pudo cargar el menú. Intenta recargar la página.</div>
            )}

            {status === "ready" && tree.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No hay opciones de menú disponibles.</div>
            )}

            {status === "ready" && tree.length > 0 && isSearching && (
              <SearchResultsList
                results={searchResults}
                isFavorite={isFavorite}
                onToggleFavorite={toggleFavorite}
                onSelectLeaf={handleLeafSelect}
              />
            )}

            {status === "ready" && tree.length > 0 && !isSearching && (
              <>
                {favoriteItems.length > 0 && (
                  <MenuSection id="favorites" title="Favoritos" count={favoriteItems.length}>
                    {favoriteItems.map((item) => (
                      <MenuItemRow
                        key={`fav-${item.id}`}
                        item={item}
                        isCurrentRouteActive={isRouteActive(item.route)}
                        isFavorite
                        onToggleFavorite={toggleFavorite}
                        onSelect={() => handleLeafSelect(item)}
                      />
                    ))}
                  </MenuSection>
                )}

                {recentItems.length > 0 && (
                  <MenuSection id="recent" title="Recientes" count={recentItems.length} defaultOpen={false}>
                    {recentItems.map((item) => (
                      <MenuItemRow
                        key={`recent-${item.id}`}
                        item={item}
                        isCurrentRouteActive={isRouteActive(item.route)}
                        isFavorite={isFavorite(item.id)}
                        onToggleFavorite={toggleFavorite}
                        onSelect={() => handleLeafSelect(item)}
                      />
                    ))}
                  </MenuSection>
                )}

                <MenuSection id="modules" title="Módulos" count={totalModuleCount}>
                  {shouldVirtualize ? (
                    <VirtualList
                      rows={visibleRows}
                      rowHeight={ROW_HEIGHT}
                      className="sidebar-scroll"
                      renderRow={(row) => (
                        <MenuItemRow
                          item={row.item}
                          depth={row.depth}
                          isOpen={Boolean(openSubMenus[row.item.id])}
                          isCurrentRouteActive={isRouteActive(row.item.route)}
                          isChildRouteActive={(row.item.children || []).some(hasActiveRouteInTree)}
                          isFavorite={isFavorite(row.item.id)}
                          onToggleFavorite={toggleFavorite}
                          onSelect={() => handleMenuSelect(row.item)}
                        />
                      )}
                    />
                  ) : (
                    <MenuTree
                      items={tree}
                      openSubMenus={openSubMenus}
                      onToggle={toggleSubMenu}
                      isRouteActive={isRouteActive}
                      hasActiveRouteInTree={hasActiveRouteInTree}
                      isFavorite={isFavorite}
                      onToggleFavorite={toggleFavorite}
                      onSelectLeaf={handleLeafSelect}
                    />
                  )}
                </MenuSection>
              </>
            )}
          </List>
        </Box>
      </SwipeableDrawer>

      <style>{`
        .sidebar-scroll { scrollbar-width: thin; scrollbar-color: rgba(100,116,139,0.35) transparent; }
        .sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background-color: rgba(100,116,139,0.35); border-radius: 999px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(100,116,139,0.55); }
        ${status === "ready"
          ? `
        /* Reserva el ancho del riel a nivel global: la mayoría de las
           páginas NO anidan su contenido dentro de <Layout>, así que un
           padding puesto en un componente de React no les llega. body es
           el único ancestro que comparten todas. Nav.js compensa esto para
           que la barra superior siga ocupando el ancho completo. */
        body { padding-left: ${DRAWER_WIDTH_COMPACT}px; }
        `
          : ""
        }
      `}</style>
    </div>
  );
}
